import { useEffect, useRef, useState } from "react";
import { useRoute, Link } from "wouter";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useAuth } from "@/lib/auth";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  ArrowLeft, MessageSquare, StickyNote, Send, Paperclip, UserCheck,
  AlertTriangle, Bot, Building2, Link2, Mic, X, ChevronDown,
} from "lucide-react";

type Ticket = {
  id: string; number: number; subject: string; description?: string | null;
  status: string; priority: string; channel: string; category?: string | null;
  departmentId?: string | null; departmentName?: string | null;
  customerName?: string | null; customerEmail?: string | null; customerPhone?: string | null;
  requesterUserId?: string | null; requesterName?: string | null; requesterEmail?: string | null;
  assignedAgentId?: string | null; assignedAgentName?: string | null;
  callSessionId?: string | null; liveChatSessionId?: string | null; tags?: string[];
  dueAt?: string | null; firstResponseAt?: string | null; createdAt: string; updatedAt: string; resolvedAt?: string | null;
  botId?: string | null; botName?: string | null;
  organizationId?: string | null;
};
type Comment = {
  id: string; authorName?: string | null; body: string; internal: boolean;
  attachmentUrl?: string | null; attachmentName?: string | null; createdAt: string;
};
type Event = {
  id: string; actorName?: string | null; type: string; field?: string | null;
  oldValue?: string | null; newValue?: string | null; note?: string | null; createdAt: string;
};
type ChatLine = { sender: string; content: string; createdAt?: string | null };
type RecordingInfo = {
  id: string; recordingUrl?: string | null; durationSeconds?: number | null; accessLevel: string;
};
type RelatedTicketRef = { id: string; number: number; subject: string; status: string };
type Detail = {
  ticket: Ticket; comments: Comment[]; events: Event[]; call: any; chatTranscript: ChatLine[];
  recording?: RecordingInfo | null; orgName?: string | null; relatedTickets?: RelatedTicketRef[];
};
type Dept = { id: string; name: string };
type AgentLite = { id: string; name: string; email?: string | null };
type UserLite = { id: string; name: string; email?: string | null };
type Macro = { id: string; title: string; body: string; createdAt: string };

type TimelineItem =
  | { kind: "comment"; at: string; data: Comment }
  | { kind: "event"; at: string; data: Event };

function eventText(e: Event): string {
  switch (e.type) {
    case "created": return "created the ticket";
    case "status_changed": return `changed status from ${e.oldValue} to ${e.newValue}`;
    case "reopened": return `reopened the ticket (${e.oldValue} → ${e.newValue})`;
    case "priority_changed": return `changed priority from ${e.oldValue} to ${e.newValue}`;
    case "category_changed": return `set category to ${e.newValue}`;
    case "department_changed": return `moved to ${e.newValue || "no department"}`;
    case "assigned": return e.newValue ? `assigned to ${e.newValue}` : `unassigned (was ${e.oldValue || "unassigned"})`;
    case "due_changed": return "updated the due date";
    case "tags_changed": return `updated tags → ${e.newValue || "none"}`;
    case "comment": return "replied to the customer";
    case "note": return "added an internal note";
    case "merged": return `merged into ticket ${e.newValue}`;
    case "merge_received": return `received merged tickets: ${e.newValue}`;
    default: return e.type.replace(/_/g, " ");
  }
}

function fmtDuration(s?: number | null) {
  if (!s) return null;
  const m = Math.floor(s / 60), sec = s % 60;
  return `${m}:${sec.toString().padStart(2, "0")}`;
}

/** Sentinel value used in the assignee Select to mean "unassign". */
const UNASSIGN_VALUE = "__unassign__";
/** Sentinel value used in the requester Select to mean "no requester". */
const UNSET_REQUESTER_VALUE = "__no_requester__";
const VALID_STATUSES = ["open", "pending", "on_hold", "solved", "closed"];

export default function TicketDetail() {
  const [, params] = useRoute("/tickets/:id");
  const id = params?.id || "";
  const { user } = useAuth();
  const detail = useQuery<Detail>({
    queryKey: ["/api/tickets", id],
    queryFn: async () => (await apiRequest("GET", `/api/tickets/${id}`)).json(),
    enabled: !!id,
  });
  const depts = useQuery<Dept[]>({ queryKey: ["/api/departments/list"] });
  const agents = useQuery<AgentLite[]>({
    queryKey: ["/api/tickets/agents"],
    queryFn: async () => (await apiRequest("GET", "/api/tickets/agents")).json(),
  });
  const requesters = useQuery<UserLite[]>({
    queryKey: ["/api/tickets/requesters"],
    queryFn: async () => (await apiRequest("GET", "/api/tickets/requesters")).json(),
  });
  const macros = useQuery<Macro[]>({
    queryKey: ["/api/ticket-macros"],
    queryFn: async () => (await apiRequest("GET", "/api/ticket-macros")).json(),
  });

  const [status, setStatus] = useState("");
  const [priority, setPriority] = useState("");
  const [category, setCategory] = useState("");
  const [departmentId, setDepartmentId] = useState("");
  const [assignedAgentId, setAssignedAgentId] = useState<string>("");
  const [dueAt, setDueAt] = useState("");
  const [requesterId, setRequesterId] = useState("");
  const [reply, setReply] = useState("");
  const [internal, setInternal] = useState(false);
  const [replyStatus, setReplyStatus] = useState(""); // optional status change on submit
  const [attachment, setAttachment] = useState<{ url: string; name: string; mime: string } | null>(null);
  const [conflictError, setConflictError] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const t = detail.data?.ticket;
  useEffect(() => {
    if (t) {
      setStatus(t.status);
      setPriority(t.priority);
      setCategory(t.category || "");
      setDepartmentId(t.departmentId || "");
      setAssignedAgentId(t.assignedAgentId || "");
      setDueAt(t.dueAt ? t.dueAt.split("T")[0] : "");
      setRequesterId(t.requesterUserId || "");
      setConflictError(false);
    }
  }, [t?.id, t?.status, t?.priority, t?.category, t?.departmentId, t?.assignedAgentId, t?.dueAt, t?.requesterUserId, t?.updatedAt]);

  const invalidateTicket = () => {
    queryClient.invalidateQueries({ queryKey: ["/api/tickets", id] });
    queryClient.invalidateQueries({ queryKey: ["/api/tickets"] });
  };

  const saveProps = useMutation({
    mutationFn: async (patch: Record<string, unknown>) =>
      (await apiRequest("PATCH", `/api/tickets/${id}`, patch)).json(),
    onSuccess: () => { setConflictError(false); invalidateTicket(); },
    onError: async (err: any) => {
      if (err?.status === 409 || err?.response?.status === 409) {
        setConflictError(true);
        invalidateTicket();
      }
    },
  });

  const addComment = useMutation({
    mutationFn: async () => {
      const payload: Record<string, unknown> = {
        body: reply,
        internal,
        ...(attachment ? {
          attachmentUrl: attachment.url,
          attachmentName: attachment.name,
          attachmentMime: attachment.mime,
        } : {}),
      };
      const res = await apiRequest("POST", `/api/tickets/${id}/comments`, payload);
      // Optional status change on submit — fire after comment succeeds.
      if (replyStatus && replyStatus !== t?.status) {
        await apiRequest("PATCH", `/api/tickets/${id}`, {
          status: replyStatus,
          expectedUpdatedAt: t?.updatedAt,
        });
      }
      return res.json();
    },
    onSuccess: () => {
      setReply("");
      setAttachment(null);
      setReplyStatus("");
      invalidateTicket();
    },
  });

  // File → base64 data URL for attachment (backend stores inline).
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setAttachment({ url: reader.result as string, name: file.name, mime: file.type });
    };
    reader.readAsDataURL(file);
    // reset so same file can be re-selected
    e.target.value = "";
  };

  if (detail.isLoading || !t)
    return <div className="p-6 text-sm text-muted-foreground" data-testid="text-loading">Loading…</div>;

  const d = detail.data!;
  const timeline: TimelineItem[] = [
    ...d.comments.map((c) => ({ kind: "comment" as const, at: c.createdAt, data: c })),
    ...d.events.map((e) => ({ kind: "event" as const, at: e.createdAt, data: e })),
  ].sort((a, b) => new Date(a.at).getTime() - new Date(b.at).getTime());

  const assigneeChanged = assignedAgentId !== (t.assignedAgentId || "");
  const serverDueAt = t.dueAt ? t.dueAt.split("T")[0] : "";
  const dueChanged = dueAt !== serverDueAt;
  const requesterChanged = requesterId !== (t.requesterUserId || "");
  const propsChanged = status !== t.status || priority !== t.priority ||
    category !== (t.category || "") || departmentId !== (t.departmentId || "") ||
    assigneeChanged || dueChanged || requesterChanged;

  function buildPatch() {
    const patch: Record<string, unknown> = {
      status, priority,
      category: category || null,
      departmentId: departmentId || null,
      expectedUpdatedAt: t!.updatedAt,
    };
    if (assigneeChanged) {
      if (assignedAgentId === UNASSIGN_VALUE || assignedAgentId === "") patch.unassignAgent = true;
      else patch.assignedAgentId = assignedAgentId;
    }
    if (dueChanged) {
      if (!dueAt) patch.unsetDue = true;
      else patch.dueAt = new Date(dueAt).toISOString();
    }
    if (requesterChanged) {
      if (!requesterId || requesterId === UNSET_REQUESTER_VALUE) patch.unsetRequester = true;
      else patch.requesterUserId = requesterId;
    }
    return patch;
  }

  const agentList = agents.data || [];
  const agentOptions: AgentLite[] = t.assignedAgentId && !agentList.some((a) => a.id === t.assignedAgentId)
    ? [{ id: t.assignedAgentId, name: t.assignedAgentName || t.assignedAgentId }, ...agentList]
    : agentList;

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto space-y-4" data-testid="page-ticket-detail">
      <Link href="/tickets" className="text-xs text-muted-foreground inline-flex items-center hover:underline" data-testid="link-back-tickets">
        <ArrowLeft className="w-3 h-3 mr-1" />Back to tickets
      </Link>

      {/* ── Header ──────────────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-start justify-between gap-2" data-testid="ticket-header">
        <div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground tabular-nums" data-testid="text-ticket-number">#{t.number}</span>
            <h1 className="text-2xl font-bold tracking-tight" data-testid="text-ticket-subject">{t.subject}</h1>
          </div>
          <div className="flex flex-wrap gap-2 mt-2">
            <Badge variant="outline" data-testid="badge-status">{status.replace(/_/g, " ")}</Badge>
            <Badge variant="secondary" data-testid="badge-priority">{priority}</Badge>
            <Badge data-testid="badge-channel">{t.channel}</Badge>
            {t.departmentName && <Badge variant="outline" data-testid="badge-department">{t.departmentName}</Badge>}
            {(t.tags || []).map((tag) => (
              <Badge key={tag} variant="outline" className="text-xs" data-testid={`badge-tag-${tag}`}>{tag}</Badge>
            ))}
            {t.callSessionId && <Badge variant="outline" data-testid="badge-linked-call">linked call</Badge>}
            {t.liveChatSessionId && <Badge variant="outline" data-testid="badge-linked-chat">from live chat</Badge>}
          </div>
        </div>
      </div>

      {conflictError && (
        <div className="flex items-center gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800" data-testid="alert-conflict">
          <AlertTriangle className="w-4 h-4 shrink-0" />
          <span>This ticket was modified by someone else while you were editing. The form has been refreshed — please review and save again.</span>
        </div>
      )}

      {/* SLA strip */}
      <Card>
        <CardContent className="p-3 flex flex-wrap gap-x-6 gap-y-1 text-xs">
          <span data-testid="text-sla-created"><span className="text-muted-foreground">Created:</span> {new Date(t.createdAt).toLocaleString()}</span>
          <span data-testid="text-sla-updated"><span className="text-muted-foreground">Updated:</span> {new Date(t.updatedAt).toLocaleString()}</span>
          <span data-testid="text-sla-first-response"><span className="text-muted-foreground">First response:</span> {t.firstResponseAt ? new Date(t.firstResponseAt).toLocaleString() : <span className="text-amber-600">pending</span>}</span>
          <span data-testid="text-sla-due"><span className="text-muted-foreground">Due:</span> {t.dueAt ? new Date(t.dueAt).toLocaleString() : "—"}</span>
          {t.resolvedAt && <span data-testid="text-sla-resolved"><span className="text-muted-foreground">Resolved:</span> {new Date(t.resolvedAt).toLocaleString()}</span>}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-[1fr_300px] gap-4">
        <div className="space-y-4">
          {t.description && (
            <Card>
              <CardHeader><CardTitle className="text-sm">Description</CardTitle></CardHeader>
              <CardContent className="text-sm whitespace-pre-wrap" data-testid="text-description">{t.description}</CardContent>
            </Card>
          )}

          {/* ── Linked call recording ──────────────────────────────────── */}
          {(d.recording?.recordingUrl || (d.call && d.call.recordingUrl)) && (
            <Card data-testid="card-recording">
              <CardHeader>
                <CardTitle className="text-sm flex items-center gap-1.5">
                  <Mic className="w-3.5 h-3.5" />Call Recording
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {(() => {
                  const recUrl = d.recording?.recordingUrl || d.call?.recordingUrl;
                  const dur = d.recording?.durationSeconds;
                  const level = d.recording?.accessLevel;
                  return (
                    <>
                      {dur && <p className="text-xs text-muted-foreground" data-testid="text-recording-duration">Duration: {fmtDuration(dur)}</p>}
                      {level && <p className="text-xs text-muted-foreground" data-testid="text-recording-access">Access: {level}</p>}
                      {/* WCAG 1.2.2: empty-src <track> removed — it advertises captions
                          that do not exist, misleading assistive technology. A proper
                          caption VTT file should be provided here when available. */}
                      {recUrl && recUrl.startsWith("http") ? (
                        <audio controls src={recUrl} className="w-full h-10" aria-label="Call recording" data-testid="audio-recording" />
                      ) : (
                        <p className="text-xs text-muted-foreground italic" data-testid="text-recording-unavailable">Recording stored; playback URL not publicly accessible in staging.</p>
                      )}
                    </>
                  );
                })()}
              </CardContent>
            </Card>
          )}
          {t.callSessionId && !d.recording?.recordingUrl && !(d.call && d.call.recordingUrl) && (
            <Card data-testid="card-no-recording">
              <CardContent className="p-3 text-xs text-muted-foreground flex items-center gap-1.5">
                <Mic className="w-3.5 h-3.5" />No recording available for this call.
              </CardContent>
            </Card>
          )}

          {/* Linked live-chat transcript */}
          {d.chatTranscript.length > 0 && (
            <Card>
              <CardHeader><CardTitle className="text-sm">Chat transcript</CardTitle></CardHeader>
              <CardContent className="space-y-1.5 text-sm" data-testid="list-transcript">
                {d.chatTranscript.map((m, i) => (
                  <div key={i} className="flex gap-2">
                    <span className="text-xs font-medium text-muted-foreground w-16 shrink-0 capitalize">{m.sender}</span>
                    <span>{m.content}</span>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* ── Timeline ────────────────────────────────────────────────── */}
          <Card>
            <CardHeader><CardTitle className="text-sm">Conversation &amp; activity</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              {timeline.length === 0 && <p className="text-xs text-muted-foreground">No activity yet.</p>}
              {timeline.map((item) => item.kind === "comment" ? (
                <div
                  key={item.data.id}
                  className={`rounded-lg border p-3 ${item.data.internal ? "bg-amber-50 border-amber-200" : "bg-muted/30"}`}
                  data-testid={`comment-${item.data.id}`}
                >
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
                    {item.data.internal ? <StickyNote className="w-3 h-3" /> : <MessageSquare className="w-3 h-3" />}
                    <span className="font-medium text-foreground">{item.data.authorName || "Agent"}</span>
                    {item.data.internal && (
                      <Badge variant="outline" className="text-[10px] py-0" data-testid={`badge-internal-${item.data.id}`}>internal note</Badge>
                    )}
                    <span className="ml-auto">{new Date(item.data.createdAt).toLocaleString()}</span>
                  </div>
                  <p className="text-sm whitespace-pre-wrap">{item.data.body}</p>
                  {item.data.attachmentUrl && (
                    <a href={item.data.attachmentUrl} download={item.data.attachmentName || "attachment"} className="inline-flex items-center gap-1 text-xs text-primary mt-1.5 hover:underline" data-testid={`attachment-${item.data.id}`}>
                      <Paperclip className="w-3 h-3" />{item.data.attachmentName || "attachment"}
                    </a>
                  )}
                </div>
              ) : (
                <div key={item.data.id} className="flex items-center gap-2 text-xs text-muted-foreground pl-1" data-testid={`event-${item.data.id}`}>
                  <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground/40 shrink-0" />
                  <span><span className="font-medium text-foreground">{item.data.actorName || "System"}</span> {eventText(item.data)}</span>
                  <span className="ml-auto">{new Date(item.data.createdAt).toLocaleString()}</span>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* ── Reply composer ──────────────────────────────────────────── */}
          <Card data-testid="card-reply-composer">
            <CardContent className="p-3 space-y-2">
              {/* Public / Internal toggle */}
              <div className="flex gap-1.5" data-testid="reply-type-toggle">
                <button
                  onClick={() => setInternal(false)}
                  className={`px-2.5 py-1 rounded-md text-xs font-medium ${!internal ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}
                  data-testid="button-tab-reply"
                ><MessageSquare className="w-3 h-3 inline mr-1" />Public reply</button>
                <button
                  onClick={() => setInternal(true)}
                  className={`px-2.5 py-1 rounded-md text-xs font-medium ${internal ? "bg-amber-500 text-white" : "bg-muted text-muted-foreground"}`}
                  data-testid="button-tab-note"
                ><StickyNote className="w-3 h-3 inline mr-1" />Internal note</button>

                {/* Macro selector */}
                {(macros.data || []).length > 0 && (
                  <Select
                    value="__none__"
                    onValueChange={(v) => {
                      if (v === "__none__") return;
                      const macro = (macros.data || []).find((m) => m.id === v);
                      if (macro) setReply((prev) => prev ? `${prev}\n\n${macro.body}` : macro.body);
                    }}
                  >
                    <SelectTrigger className="h-7 w-36 text-xs ml-auto" aria-label="Insert macro" data-testid="select-macro">
                      <ChevronDown className="w-3 h-3 mr-1" />
                      <span>Insert macro</span>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__" disabled>Insert macro…</SelectItem>
                      {(macros.data || []).map((m) => (
                        <SelectItem key={m.id} value={m.id} data-testid={`macro-option-${m.id}`}>{m.title}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>

              <Textarea
                value={reply}
                onChange={(e) => setReply(e.target.value)}
                rows={3}
                placeholder={internal ? "Add a private note for your team…" : "Reply to the customer…"}
                aria-label={internal ? "Private note" : "Reply to customer"}
                data-testid="input-reply"
              />

              {/* Attachment row */}
              <div className="flex items-center gap-2 flex-wrap">
                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  onChange={handleFileChange}
                  data-testid="file-input-attachment"
                  accept="image/*,application/pdf,.txt,.csv,.doc,.docx,.xls,.xlsx,.zip"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs px-2"
                  onClick={() => fileInputRef.current?.click()}
                  data-testid="button-attach"
                >
                  <Paperclip className="w-3 h-3 mr-1" />Attach file
                </Button>
                {attachment && (
                  <div className="flex items-center gap-1 text-xs bg-muted rounded px-2 py-1" data-testid="attachment-preview">
                    <Paperclip className="w-3 h-3 text-muted-foreground" />
                    <span className="max-w-[120px] truncate">{attachment.name}</span>
                    <button
                      onClick={() => setAttachment(null)}
                      className="ml-1 text-muted-foreground hover:text-destructive"
                      aria-label="Remove attachment"
                      data-testid="button-remove-attachment"
                    ><X className="w-3 h-3" /></button>
                  </div>
                )}

                {/* Status-on-submit */}
                <div className="ml-auto flex items-center gap-1.5">
                  <Select
                    value={replyStatus || "__keep__"}
                    onValueChange={(v) => setReplyStatus(v === "__keep__" ? "" : v)}
                  >
                    <SelectTrigger className="h-7 w-44 text-xs" aria-label="Change ticket status on reply" data-testid="select-reply-status">
                      <SelectValue placeholder="Keep status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__keep__">Keep current status</SelectItem>
                      {VALID_STATUSES.map((s) => (
                        <SelectItem key={s} value={s} data-testid={`reply-status-${s}`}>
                          {s === t.status ? `${s.replace(/_/g, " ")} (current)` : s.replace(/_/g, " ")}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    size="sm"
                    onClick={() => addComment.mutate()}
                    disabled={!reply.trim() || addComment.isPending}
                    data-testid="button-send-reply"
                  >
                    <Send className="w-3.5 h-3.5 mr-1" />{addComment.isPending ? "Sending…" : internal ? "Add note" : "Send reply"}
                  </Button>
                </div>
              </div>

              {/* Related tickets — shown below composer when merged relationships exist */}
              {(d.relatedTickets || []).length > 0 && (
                <div className="pt-1 border-t mt-2" data-testid="related-tickets">
                  <p className="text-xs font-medium text-muted-foreground mb-1.5 flex items-center gap-1">
                    <Link2 className="w-3 h-3" />Related tickets
                  </p>
                  <div className="space-y-1">
                    {(d.relatedTickets || []).map((rt) => (
                      <Link
                        key={rt.id}
                        href={`/tickets/${rt.id}`}
                        className="flex items-center justify-between text-xs rounded px-2 py-1 bg-muted/40 hover:bg-muted transition-colors"
                        data-testid={`related-ticket-${rt.id}`}
                      >
                        <span className="font-medium text-muted-foreground mr-1">#{rt.number}</span>
                        <span className="flex-1 truncate">{rt.subject}</span>
                        <Badge variant="outline" className="text-[10px] py-0 ml-2 shrink-0">{rt.status.replace(/_/g, " ")}</Badge>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* ── Sidebar ───────────────────────────────────────────────────── */}
        <div className="space-y-4">

          {/* Properties */}
          <Card>
            <CardHeader><CardTitle className="text-sm">Properties</CardTitle></CardHeader>
            <CardContent className="space-y-2.5">
              <div>
                <span id="ticket-props-status-label" className="text-xs text-muted-foreground">Status</span>
                <Select value={status} onValueChange={setStatus}>
                  <SelectTrigger aria-labelledby="ticket-props-status-label" data-testid="select-ticket-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="open">Open</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="on_hold">On hold</SelectItem>
                    <SelectItem value="solved">Solved</SelectItem>
                    <SelectItem value="closed">Closed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <span id="ticket-props-priority-label" className="text-xs text-muted-foreground">Priority</span>
                <Select value={priority} onValueChange={setPriority}>
                  <SelectTrigger aria-labelledby="ticket-props-priority-label" data-testid="select-ticket-priority"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <span id="ticket-props-department-label" className="text-xs text-muted-foreground">Department</span>
                <Select value={departmentId || "none"} onValueChange={(v) => setDepartmentId(v === "none" ? "" : v)}>
                  <SelectTrigger aria-labelledby="ticket-props-department-label" data-testid="select-ticket-department"><SelectValue placeholder="No department" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No department</SelectItem>
                    {(depts.data || []).map((dep) => <SelectItem key={dep.id} value={dep.id}>{dep.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label htmlFor="ticket-props-category" className="text-xs text-muted-foreground">Category</label>
                <Input id="ticket-props-category" value={category} onChange={(e) => setCategory(e.target.value)} placeholder="e.g. billing" data-testid="input-ticket-category" />
              </div>
              <div>
                <label htmlFor="ticket-props-due" className="text-xs text-muted-foreground">Due date</label>
                <Input id="ticket-props-due" type="date" value={dueAt} onChange={(e) => setDueAt(e.target.value)} data-testid="input-ticket-due" />
              </div>
              <div>
                <span id="ticket-props-requester-label" className="text-xs text-muted-foreground">Requester (org user)</span>
                <Select value={requesterId || UNSET_REQUESTER_VALUE} onValueChange={(v) => setRequesterId(v === UNSET_REQUESTER_VALUE ? "" : v)}>
                  <SelectTrigger aria-labelledby="ticket-props-requester-label" data-testid="select-ticket-requester">
                    <SelectValue placeholder="No requester" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={UNSET_REQUESTER_VALUE}>No requester</SelectItem>
                    {(requesters.data || []).map((u) => (
                      <SelectItem key={u.id} value={u.id}>{u.name}{u.email ? ` (${u.email})` : ""}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button
                className="w-full" size="sm"
                disabled={!propsChanged || saveProps.isPending}
                onClick={() => saveProps.mutate(buildPatch())}
                data-testid="button-save-ticket"
              >{saveProps.isPending ? "Saving…" : "Save changes"}</Button>
            </CardContent>
          </Card>

          {/* Assignment */}
          <Card>
            <CardHeader><CardTitle className="text-sm">Assignment</CardTitle></CardHeader>
            <CardContent className="space-y-2.5">
              <div>
                <span id="ticket-assignee-label" className="text-xs text-muted-foreground">Assigned to</span>
                <Select value={assignedAgentId || UNASSIGN_VALUE} onValueChange={(v) => setAssignedAgentId(v === UNASSIGN_VALUE ? "" : v)}>
                  <SelectTrigger aria-labelledby="ticket-assignee-label" data-testid="select-ticket-assignee">
                    <SelectValue placeholder="Unassigned" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={UNASSIGN_VALUE}>Unassigned</SelectItem>
                    {agentOptions.map((a) => (
                      <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {user?.id && assignedAgentId !== user.id && agentList.some((a) => a.id === user.id) && (
                <Button
                  variant="outline" size="sm" className="w-full"
                  disabled={saveProps.isPending}
                  onClick={() => {
                    setAssignedAgentId(user.id);
                    saveProps.mutate({ assignedAgentId: user.id, expectedUpdatedAt: t.updatedAt });
                  }}
                  data-testid="button-assign-me"
                ><UserCheck className="w-3.5 h-3.5 mr-1" />Assign to me</Button>
              )}
            </CardContent>
          </Card>

          {/* Customer / Requester */}
          <Card data-testid="card-requester">
            <CardHeader><CardTitle className="text-sm">Customer / Requester</CardTitle></CardHeader>
            <CardContent className="text-xs space-y-1">
              {t.requesterUserId && (
                <div className="mb-1 pb-1 border-b" data-testid="requester-linked">
                  <p className="text-muted-foreground font-medium mb-0.5">Linked requester</p>
                  <div><span className="text-muted-foreground">Name:</span> {t.requesterName || "—"}</div>
                  <div><span className="text-muted-foreground">Email:</span> {t.requesterEmail || "—"}</div>
                </div>
              )}
              <div data-testid="customer-name"><span className="text-muted-foreground">Customer name:</span> {t.customerName || "—"}</div>
              <div data-testid="customer-email"><span className="text-muted-foreground">Customer email:</span> {t.customerEmail || "—"}</div>
              <div data-testid="customer-phone"><span className="text-muted-foreground">Phone:</span> {t.customerPhone || "—"}</div>
            </CardContent>
          </Card>

          {/* Organization */}
          <Card data-testid="card-organization">
            <CardHeader>
              <CardTitle className="text-sm flex items-center gap-1.5">
                <Building2 className="w-3.5 h-3.5" />Organization
              </CardTitle>
            </CardHeader>
            <CardContent className="text-xs space-y-1">
              <div data-testid="org-name"><span className="text-muted-foreground">Name:</span> {d.orgName || "—"}</div>
              <div data-testid="org-id"><span className="text-muted-foreground">ID:</span> <span className="font-mono">{t.organizationId}</span></div>
            </CardContent>
          </Card>

          {/* Linked bot */}
          <Card data-testid="card-linked-bot">
            <CardHeader>
              <CardTitle className="text-sm flex items-center gap-1.5">
                <Bot className="w-3.5 h-3.5" />Linked Bot
              </CardTitle>
            </CardHeader>
            <CardContent className="text-xs">
              {t.botId ? (
                <div className="space-y-1" data-testid="linked-bot-present">
                  <div><span className="text-muted-foreground">Name:</span> {t.botName || "—"}</div>
                  <Link
                    href={`/chatbots/${t.botId}`}
                    className="text-primary hover:underline"
                    data-testid="linked-bot-link"
                  >Open bot →</Link>
                </div>
              ) : (
                <p className="text-muted-foreground" data-testid="linked-bot-none">No linked bot.</p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
